import { useEffect, useState } from 'react';

declare global {
  interface Window {
    google?: any;
    __lorescoutMapsInit?: () => void;
    __lorescoutMapsReady?: boolean;
  }
}

const BROWSER_KEY = import.meta.env.VITE_LOVABLE_CONNECTOR_GOOGLE_MAPS_BROWSER_KEY as string | undefined;
const CHANNEL = import.meta.env.VITE_LOVABLE_CONNECTOR_GOOGLE_MAPS_TRACKING_ID as string | undefined;

let loaderPromise: Promise<void> | null = null;

function loadMaps(): Promise<void> {
  if (typeof window === 'undefined') return Promise.reject(new Error('No window'));
  if (window.__lorescoutMapsReady && window.google?.maps) return Promise.resolve();
  if (loaderPromise) return loaderPromise;
  if (!BROWSER_KEY) return Promise.reject(new Error('Google Maps key missing'));

  loaderPromise = new Promise<void>((resolve, reject) => {
    window.__lorescoutMapsInit = () => {
      window.__lorescoutMapsReady = true;
      resolve();
    };
    const script = document.createElement('script');
    const params = new URLSearchParams({
      key: BROWSER_KEY,
      loading: 'async',
      callback: '__lorescoutMapsInit',
    });
    if (CHANNEL) params.set('channel', CHANNEL);
    script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
    script.async = true;
    script.defer = true;
    script.onerror = () => reject(new Error('Failed to load Google Maps'));
    document.head.appendChild(script);
  });
  return loaderPromise;
}

export function useGoogleMaps() {
  const [ready, setReady] = useState<boolean>(!!window?.__lorescoutMapsReady);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    loadMaps()
      .then(() => { if (!cancelled) setReady(true); })
      .catch(err => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, []);
  return { ready, error };
}
